import React, { useState } from 'react';
import { T } from './shared/constants';
import { Btn, Card } from './shared/ui';
import NaplanItemRenderer from './QuestionRenderers/NaplanItemRenderer';
import { isCorrectResponse, retryHint, itemType } from './QuestionRenderers/naplanItems';
import { getCounts, getDue, recordReview, nextDueAt } from '../lib/glowUp';

// Glow Up: review the questions this child got wrong until each one is Shining. See src/lib/glowUp.js for the rules.

const PILES = [
  { key: 'dim', label: 'Dim', blurb: 'Ready to fix', icon: '🕯️', color: T.textDim, bg: '#F1F3F2' },
  { key: 'glowing', label: 'Glowing', blurb: 'Fixed — prove it twice', icon: '✨', color: T.gold, bg: T.goldLo },
  { key: 'shining', label: 'Shining', blurb: 'Mastered', icon: '🌟', color: T.accent, bg: T.accentLo },
];
const SESSION_SIZE = 10;
const EXAM_NAMES = { naplan: 'NAPLAN', selective_nsw: 'NSW Selective', oc: 'OC Test', acer: 'ACER Scholarship', icas: 'ICAS', ignite: 'IGNITE', vic_selective: 'VIC Selective', hsc: 'HSC', vce: 'VCE' };

function sourceLine(it) {
  const year = it.year ? `Year ${String(it.year).replace(/^y/i, '')}` : '';
  const subject = it.subject ? it.subject.charAt(0).toUpperCase() + it.subject.slice(1) : '';
  return [EXAM_NAMES[it.examProgram] || '', year, subject].filter(Boolean).join(' · ');
}

function whenText(ts) {
  if (!ts) return '';
  const days = Math.round((new Date(ts).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 864e5);
  return days <= 0 ? 'later today' : days === 1 ? 'tomorrow' : `in ${days} days`;
}

export function GlowUpCard({ profileId, onOpen }) {
  const c = getCounts(profileId);
  if (!c.total) return null;
  return (
    <button onClick={onOpen} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left', cursor: 'pointer',
      background: `linear-gradient(135deg, ${T.goldLo}, #FFFFFF)`, border: `1.5px solid ${T.gold}55`, borderRadius: 14, padding: '14px 18px',
      fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <span style={{ fontSize: 26 }}>✨</span>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 800, color: T.text }}>Glow Up</span>
        <span style={{ display: 'block', fontSize: 12, color: T.textMid }}>
          {c.ready ? `${c.ready} question${c.ready === 1 ? '' : 's'} ready to glow up` : 'All caught up'} · {c.shining} Shining
        </span>
      </span>
      {c.ready > 0 && <span style={{ background: T.gold, color: 'white', borderRadius: 999, padding: '3px 10px', fontSize: 12, fontWeight: 800 }}>{c.ready}</span>}
    </button>
  );
}

export default function GlowUp({ profileId, onExit, onAskLumi }) {
  const [counts, setCounts] = useState(() => getCounts(profileId));
  const [queue, setQueue] = useState(null);      // items in the current review session, or null on the home view
  const [idx, setIdx] = useState(0);
  const [attempt, setAttempt] = useState(1);     // 1 = first try, 2 = after help
  const [help, setHelp] = useState(null);        // feedback for the first wrong answer
  const [final, setFinal] = useState(null);      // { raw, correct } once this item is finished
  const [results, setResults] = useState([]);    // { id, outcome, stage }

  function start() {
    const due = getDue(profileId, { limit: SESSION_SIZE });
    if (!due.length) return;
    setQueue(due); setIdx(0); setAttempt(1); setHelp(null); setFinal(null); setResults([]);
  }

  function finish(it, outcome, raw, correct) {
    const stage = recordReview(profileId, it.id, outcome);
    setFinal({ raw, correct });
    setResults((r) => [...r, { id: it.id, outcome, stage }]);
  }

  function onSubmit(it, answer) {
    const q = it.question;
    const raw = answer.rawAnswer;
    const correct = isCorrectResponse(q, raw);
    if (correct) return finish(it, attempt === 1 ? 'first' : 'helped', raw, true);
    if (attempt === 1) {
      // feedback for the answer they chose, with the answer itself taken out — the retry has to be theirs
      setHelp(retryHint(q, raw) || (q.hint ? 'Use the hint below and check each choice again.' : 'Read the question again carefully and check each choice.'));
      setAttempt(2);
      return;
    }
    finish(it, 'wrong', raw, false);
  }

  function next() {
    if (idx + 1 < queue.length) { setIdx(idx + 1); setAttempt(1); setHelp(null); setFinal(null); }
    else { setQueue([]); setCounts(getCounts(profileId)); }
  }

  const page = { flex: 1, overflowY: 'auto', background: T.bg, minHeight: 'calc(100vh - 58px)' }; // fill below the app header
  const header = (title, onBack) => (
    <div style={{ padding: '14px 24px', borderBottom: `1px solid ${T.border}`, background: T.surface, display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 10 }}>
      <button onClick={onBack} aria-label="Back" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, width: 32, height: 32, color: T.textMid, cursor: 'pointer' }}>←</button>
      <span style={{ fontSize: 15, fontWeight: 800, color: T.text }}>✨ {title}</span>
    </div>
  );

  // ── Session summary ──
  if (queue && queue.length === 0) {
    const shone = results.filter((r) => r.stage === 3).length;
    const fixed = results.filter((r) => r.stage > 0).length;
    return (
      <div style={page}>
        {header('Glow Up', onExit)}
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '40px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 8 }}>{shone ? '🌟' : '✨'}</div>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 30, color: T.text, margin: '0 0 8px' }}>Nice work!</h2>
          <p style={{ fontSize: 15, color: T.textMid, margin: '0 0 24px' }}>
            You glowed up {fixed} of {results.length} question{results.length === 1 ? '' : 's'}{shone ? `, and ${shone} ${shone === 1 ? 'is' : 'are'} now Shining` : ''}.
          </p>
          <Piles counts={counts} />
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 28 }}>
            {counts.ready > 0 && <Btn onClick={start}>Keep going ({counts.ready} ready)</Btn>}
            <Btn variant="ghost" onClick={() => setQueue(null)}>Done</Btn>
          </div>
        </div>
      </div>
    );
  }

  // ── Reviewing ──
  if (queue) {
    const it = queue[idx];
    const q = it.question;
    return (
      <div style={page}>
        {header(`Glow Up · ${idx + 1} of ${queue.length}`, () => { setQueue(null); setCounts(getCounts(profileId)); })}
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '16px 32px 0' }}>
          <p style={{ fontSize: 12, color: T.textDim, margin: 0 }}>
            {sourceLine(it)} · {it.stage === 0 ? 'Fix it' : 'Prove it'}
          </p>
          {help && !final && (
            <Card style={{ marginTop: 12, background: T.goldLo, borderColor: `${T.gold}66`, padding: '14px 18px' }}>
              <p style={{ fontSize: 13, fontWeight: 800, color: T.gold, margin: '0 0 4px' }}>Not quite — have another go</p>
              <p style={{ fontSize: 14, color: T.text, margin: 0, lineHeight: 1.6 }}>{help}</p>
            </Card>
          )}
          {final && (
            <Card style={{ marginTop: 12, background: final.correct ? T.greenLo : T.redLo, borderColor: final.correct ? T.green : T.red, padding: '14px 18px' }}>
              <p style={{ fontSize: 14, fontWeight: 800, color: final.correct ? T.green : T.red, margin: 0 }}>
                {final.correct
                  ? (results[results.length - 1]?.stage === 3 ? '🌟 Shining! You have mastered this one.'
                    : attempt === 1 ? `✨ Glowing! We'll check it again ${whenText(Date.now() + (it.stage === 0 ? 1 : 4) * 864e5)}.`
                    : "✨ Fixed! We'll check it again tomorrow to make sure it sticks.")
                  : "Here's how it works. This one will come back tomorrow."}
              </p>
            </Card>
          )}
        </div>
        <NaplanItemRenderer
          key={`${it.id}:${attempt}`}
          question={q}
          studentAnswer={final ? { questionId: q.id, questionType: itemType(q), rawAnswer: final.raw, isCorrect: final.correct, submittedAt: new Date().toISOString() } : null}
          state={{ mode: attempt === 2 && !final ? 'practice' : 'review', current: idx + 1, total: queue.length }}
          showExplanation={!!final}
          onSubmit={(answer) => onSubmit(it, answer)}
          onAskLumi={() => onAskLumi && onAskLumi(q)}
          onExplainVideo={() => {}}
        />
        {final && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '0 32px 64px' }}>
            <Btn onClick={next}>{idx + 1 < queue.length ? 'Next →' : 'Finish'}</Btn>
          </div>
        )}
      </div>
    );
  }

  // ── Home ──
  const nextAt = nextDueAt(profileId);
  return (
    <div style={page}>
      {header('Glow Up', onExit)}
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 24px' }}>
        <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 30, color: T.text, margin: '0 0 6px' }}>Turn mistakes into skills</h2>
        <p style={{ fontSize: 14, color: T.textMid, margin: '0 0 24px', lineHeight: 1.6 }}>
          Every question you get wrong lands here. Fix it, then prove it twice — tomorrow and a few days later — and it becomes Shining.
        </p>
        <Piles counts={counts} />
        <div style={{ marginTop: 28, textAlign: 'center' }}>
          {counts.ready > 0
            ? <Btn onClick={start}>Start Glow Up ({Math.min(counts.ready, SESSION_SIZE)} question{Math.min(counts.ready, SESSION_SIZE) === 1 ? '' : 's'})</Btn>
            : <p style={{ fontSize: 14, color: T.textMid }}>
                {counts.total === 0 ? 'Nothing here yet. When you get a question wrong in practice, it will appear here.'
                  : nextAt ? `All caught up! Your next check is ${whenText(nextAt)}.` : 'Everything is Shining. Brilliant!'}
              </p>}
        </div>
      </div>
    </div>
  );
}

function Piles({ counts }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
      {PILES.map((p) => (
        <div key={p.key} style={{ background: p.bg, border: `1px solid ${p.color}33`, borderRadius: 14, padding: '16px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 24 }}>{p.icon}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: p.color, lineHeight: 1.2 }}>{counts[p.key]}</div>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{p.label}</div>
          <div style={{ fontSize: 11, color: T.textMid }}>{p.blurb}</div>
        </div>
      ))}
    </div>
  );
}
